import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  consultationList,
  detailUser,
  updateConsultationStatus,
} from "./callbacks";
import { IConsultationBookingPayloadType } from "../../../types/api/consultation-book-types";

export const useUpdateConsultationStatusServiceUpdate = (
  {
    qData = { page: 1, limit: 10 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IConsultationBookingPayloadType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IConsultationBookingPayloadType>,
    unknown,
    IConsultationBookingPayloadType
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await updateConsultationStatus(data);
      return response.data;
    },

    onSuccess: (res, variables: any, context,) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.CONSULTATION_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          const exists = old.data.some((v: any) => v._id === res.data._id);
          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CONSULTATION_DETAIL, variables?._id],
      });

      onSuccess?.(res);
      (options?.onSuccess as any)?.(res, variables, context);
    },

    onError: (error, variables, context) => {
      onError?.(error);
      (options?.onError as any)?.(error, variables, context);
    },

    ...options,
  });
};

export const useConsultationList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<
      TResponseWithQData<IConsultationBookingPayloadType[]>,
      Error
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<
  TResponseWithQData<IConsultationBookingPayloadType[]>,
  Error
> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<
    TResponseWithQData<IConsultationBookingPayloadType[]>,
    Error
  >({
    queryKey: [
      API_ENDPOINTS.CONSULTATION_LIST,
      { ...queryParams, total: null },
    ],
    queryFn: async () => {
      const res = await consultationList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: true,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...params }));
    },
  };
};

export const useConsultationDetail = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IConsultationBookingPayloadType>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IConsultationBookingPayloadType>, Error> => {
  return useQuery<TResponse<IConsultationBookingPayloadType>, Error>({
    queryKey: [API_ENDPOINTS.CONSULTATION_DETAIL, id],
    queryFn: async () => {
      const res = await detailUser(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    staleTime: 300000,
    ...options,
  });
};

// export const useServiceDelete = (
//   { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
//     qData?: QData;
//     onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
//     onError?: (error: unknown) => void;
//   }
//   ,
//   options: UseMutationOptions<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> = {}
// ): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
//   const queryClient = useQueryClient();

//   return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
//     mutationFn: async (data: IDeleteDataResponseAndPayload) => {
//       const response = await deleteService(data);
//       return response.data;
//     },
//     onSuccess: (res: TResponse<IDeleteDataResponseAndPayload>, ...rest) => {
//       console.log(API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null })
//       console.log(res,"res-delete")
//       queryClient.setQueryData([API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null }], (old: TResponse<IDeleteDataResponseAndPayload[]>) => {
//         if (!old) return;
//         return {
//           ...old,
//           data: Array.isArray(res.data.ids) ? old.data.filter((v: any) => ~res.data.ids.includes(v._id)) : old.data.filter((v: any) => res.data.ids !== v._id),
//         };
//       });

//       onSuccess?.(res);
//       options?.onSuccess?.(res, ...rest);
//     },
//     onError: (error, ...rest) => {
//       onError?.(error);
//       options?.onError?.(error, ...rest);
//     },
//     ...options
//   });
// };
